import { useEffect, useState } from 'react';

const DEFAULT_NAV_HEIGHT_PX = 80;

// Selector picks the site navigation specifically, not the docs TOC
// (which is also a `<nav>`). The TOC sets `aria-label="Table of
// contents"`, so excluding that label finds the chrome navbar.
const NAV_SELECTOR = 'nav:not([aria-label="Table of contents"])';

/**
 * Measures the height of the site navigation bar so docs-style pages
 * can carve out exactly the remaining viewport height for their
 * scrollable area (used as a CSS custom property in `DocsLayout`).
 *
 * Re-measures on `window 'resize'` and via a `ResizeObserver` on the
 * navbar itself — the latter catches font-load / mobile vs. desktop
 * collapse / etc. that change the bar's height without firing a
 * window resize. Returns a sensible default (`80`) before the first
 * measurement and if the navbar is not in the DOM yet.
 */
export function useSiteNavHeight(): number {
  const [navHeight, setNavHeight] = useState(DEFAULT_NAV_HEIGHT_PX);

  useEffect(() => {
    const navEl = document.querySelector<HTMLElement>(NAV_SELECTOR);
    if (!navEl) return;
    const update = () =>
      setNavHeight(Math.round(navEl.getBoundingClientRect().height));
    update();
    const ro = new ResizeObserver(update);
    ro.observe(navEl);
    window.addEventListener('resize', update);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', update);
    };
  }, []);

  return navHeight;
}
